/**
 * Rubric Guide Types Configuration
 *
 * Defines all available rubric guide types with their descriptions,
 * best use cases, and included features.
 *
 * This file can be easily modified to add new guide types or update existing ones.
 */

export interface RubricGuideType {
  value: string;
  label: string;
  description: string;
  bestFor: string[];
  includes: string[];
  example?: string;
  recommended?: boolean;
  exampleRubric?: string;  // Optional example rubric in JSON format for AI template
}

export const RUBRIC_GUIDE_TYPES: RubricGuideType[] = [
  {
    value: 'ieb_standard',
    label: 'IEB Standard',
    description: 'Aligned with IEB (Independent Examinations Board) assessment standards for South African curriculum.',
    bestFor: ['PAT Projects', 'Tests', 'Exams', 'Formal Assessments'],
    includes: [
      'Cognitive levels (Knowledge, Routine, Complex, Problem-solving)',
      'Detailed marking guidance with examples',
      'Common mistakes and exemplar indicators',
      'Clear mark allocation per cognitive level',
    ],
    recommended: true,
    exampleRubric: JSON.stringify({
      "2.7": {
        "name": "mouseClicked Event Implementation",
        "description": "Implement interactive mouse click functionality to draw hockey players at click positions (Cognitive Level: Complex)",
        "maxPoints": 6,
        "scoringGuide": {
          "0": "Not attempted or no mouseClicked function present",
          "1": "mouseClicked event declared but with major structural errors. Common mistake: placing mouseClicked inside the drawPlayer function instead of outside, or incorrect function syntax",
          "2": "mouseClicked function coded outside drawPlayer function (✓) with correct assignment syntax using = function() (✓), but function body empty or incorrect",
          "3": "mouseClicked coded outside drawPlayer (✓), function assignment syntax correct (✓), drawPlayer function called inside event (✓), but parameters missing or incorrect. Common mistake: calls drawPlayer() with no parameters or wrong variables",
          "4": "mouseClicked outside drawPlayer (✓), function assignment correct (✓), drawPlayer called (✓), mouseX passed as 1st parameter (✓), but mouseY parameter missing, incorrect, or in wrong position",
          "5": "All elements present: mouseClicked outside drawPlayer (✓), function assignment syntax (✓), drawPlayer called (✓), mouseX as 1st parameter (✓), mouseY as 2nd parameter (✓), but minor syntax issue (missing semicolon, bracket, or spacing)",
          "6": "Exemplary: Complete correct implementation with all 6 elements - (1) mouseClicked event coded, (2) coded outside drawPlayer function, (3) proper function assignment using = function() syntax, (4) drawPlayer function called inside event, (5) mouseX passed as 1st parameter, (6) mouseY passed as 2nd parameter. Clean, executable code."
        }
      }
    }, null, 2),
  },
  {
    value: 'analytic',
    label: 'Analytic (Most Detailed)',
    description: 'Comprehensive rubric that breaks down each skill or criterion separately with detailed scoring guides.',
    bestFor: ['Complex projects', 'Multi-faceted assignments', 'Detailed feedback needs'],
    includes: [
      'Separate scoring for each criterion',
      'Detailed marking guidance per criterion',
      'Common mistakes for each skill',
      'Exemplar indicators of excellence',
      'Progressive scoring descriptions',
    ],
  },
  {
    value: 'holistic',
    label: 'Holistic',
    description: 'Overall assessment of work quality with a single score representing the complete performance.',
    bestFor: ['Creative work', 'Essays', 'Presentations', 'Subjective assessments'],
    includes: [
      'Descriptive performance levels (Beginning, Developing, Proficient, Exemplary)',
      'Overall quality indicators',
      'Integrated assessment criteria',
      'Qualitative feedback focus',
    ],
  },
  {
    value: 'point_based_simple',
    label: 'Point-Based Simple',
    description: 'Straightforward rubric with points and brief descriptors for quick, objective marking.',
    bestFor: ['Objective assessments', 'Quick marking needs', 'Clear-cut criteria'],
    includes: [
      'Simple point allocation',
      'Brief descriptors per level',
      'Minimal detailed guidance',
      'Fast marking process',
    ],
  },
  {
    value: 'developmental',
    label: 'Developmental',
    description: 'Shows learning progression with formative feedback focus and growth-oriented language.',
    bestFor: ['Formative assessments', 'Growth tracking', 'Skill development monitoring'],
    includes: [
      'Learning progression indicators',
      'Strengths and growth areas',
      'Next steps for improvement',
      'Developmental milestones',
    ],
  },
  {
    value: 'standards_based',
    label: 'Standards-Based',
    description: 'Aligned to specific curriculum standards or learning outcomes with clear mastery indicators.',
    bestFor: ['Curriculum-aligned assessments', 'Standards tracking', 'Competency-based grading'],
    includes: [
      'Alignment to specific standards/outcomes',
      'Mastery level indicators',
      'Evidence of standard achievement',
      'Clear connection to curriculum',
    ],
  },
  {
    value: 'single_point',
    label: 'Single-Point',
    description: 'Describes only proficient performance; teachers note what exceeds or falls below the standard.',
    bestFor: ['Differentiated feedback', 'Quick assessment', 'Flexible evaluation'],
    includes: [
      'Proficiency criteria only',
      'Space for above/below notes',
      'Personalized feedback areas',
      'Efficient marking process',
    ],
  },
  {
    value: 'checklist',
    label: 'Checklist Style',
    description: 'Yes/No or Present/Absent format for clear, objective requirements.',
    bestFor: ['Procedural tasks', 'Lab work', 'Technical requirements', 'Completion tracking'],
    includes: [
      'Binary scoring (Yes/No, Present/Absent)',
      'Clear requirement list',
      'Objective criteria',
      'Quick verification process',
    ],
  },
];

// Type for the guide type values
export type RubricGuideTypeValue = typeof RUBRIC_GUIDE_TYPES[number]['value'];

// Helper function to get guide type by value
export const getGuideTypeByValue = (value: string): RubricGuideType | undefined => {
  return RUBRIC_GUIDE_TYPES.find(type => type.value === value);
};

// Helper function to get recommended guide type
export const getRecommendedGuideType = (): RubricGuideType => {
  return RUBRIC_GUIDE_TYPES.find(type => type.recommended) || RUBRIC_GUIDE_TYPES[0];
};
